import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import Navbar from '../components/Navbar';
import API from '../utils/api';
import { useAuth } from '../contexts/AuthContext';

const AdminDashboard = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [actionModal, setActionModal] = useState({ isOpen: false, userId: null, type: 'fund', userEmail: '' });
  const [amount, setAmount] = useState('');
  const [kycModal, setKycModal] = useState({ open: false, user: null });
  const navigate = useNavigate();
  const { isLoggedIn, user } = useAuth();
  const [currency, setCurrency] = useState('USD');

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await API.get('/admin/users');
      setUsers(response.data);
    } catch (error) {
      toast.error('Failed to load users!');
      if (error.response?.status === 403) navigate('/login');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (!isLoggedIn) {
      navigate('/login');
      return;
    }
    if (user?.role !== 'admin') {
      toast.error('Admin access only!');
      navigate('/dashboard');
      return;
    }
    fetchUsers();
  }, [isLoggedIn, user, navigate, fetchUsers]);

  const openActionModal = (userId, type) => {
    setActionModal({ isOpen: true, userId, type });
    setAmount('');
  };

  const closeActionModal = () => {
    setActionModal({ isOpen: false, userId: null, type: '' });
  };

const handleBalanceUpdate = async () => {
    if (!amount || isNaN(amount) || parseFloat(amount) <= 0) {
      toast.error('Enter a valid positive amount!');
      return;
    }
    setUpdating(true);
    try {
      const updateAmount = actionModal.type === 'debit' ? -parseFloat(amount) : parseFloat(amount);
      const payload = { amount: updateAmount, currency };
      await API.post(`/admin/users/${actionModal.userId}/balance`, payload);
      toast.success(`${actionModal.type.charAt(0).toUpperCase() + actionModal.type.slice(1)}ed ${amount} ${currency} successfully!`);
      fetchUsers();
      closeActionModal();
      setAmount('');
      setCurrency('USD');
    } catch (error) {
      console.error('Fund error:', error);
      toast.error(error.response?.data?.error || 'Fund failed—check console!');
    } finally {
      setUpdating(false);
    }
  };

  const handleSuspend = async (userId) => {
    if (!window.confirm('Suspend/unsuspend this user?')) return;
    setUpdating(true);
    try {
      await API.patch(`/admin/users/${userId}/suspend`);
      toast.success('Status updated!');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Update failed!');
    } finally {
      setUpdating(false);
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Delete this user permanently?')) return;
    setUpdating(true);
    try {
      await API.delete(`/admin/users/${userId}`);
      toast.success('User deleted!');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Delete failed!');
    } finally {
      setUpdating(false);
    }
  };

  // NEW: KYC Action Handler
  const handleKycAction = async (userId, action) => {
    let reason = '';
    if (action === 'reject') {
      reason = window.prompt('Reason for rejection:');
      if (!reason) return;
    }

    if (!window.confirm(`Are you sure you want to ${action} this KYC?`)) return;

    setUpdating(true);
    try {
      await API.post(`/admin/users/${userId}/kyc`, { action, reason });
      toast.success(`KYC ${action}ed!`);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.error || 'KYC action failed');
    } finally {
      setUpdating(false);
    }
  };

  const viewKyc = (user) => {
    setKycModal({ open: true, user });
  };

  const closeKycModal = () => {
    setKycModal({ open: false, user: null });
  };

  if (loading) return <div className="min-h-screen bg-white flex items-center justify-center"><Navbar /><p>Loading...</p></div>;

  return (
    <div className="min-h-screen bg-white pt-20">
      <Navbar />
      <div className="container mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold text-primary mb-8">Admin Dashboard</h1>
        <p className="text-secondary mb-6">Manage users, balances, and KYC.</p>

        {/* Users Table */}
        <div className="bg-white rounded-lg shadow-md border border-gray-200 overflow-x-auto">
          <table className="w-full min-w-max">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 sm:px-6 sm:py-3 text-left text-xs font-medium text-secondary uppercase tracking-wider">Email</th>
                <th className="px-4 py-2 sm:px-6 sm:py-3 text-left text-xs font-medium text-secondary uppercase tracking-wider">Balance</th>
                <th className="px-4 py-2 sm:px-6 sm:py-3 text-left text-xs font-medium text-secondary uppercase tracking-wider">Status</th>
                <th className="px-4 py-2 sm:px-6 sm:py-3 text-left text-xs font-medium text-secondary uppercase tracking-wider">KYC</th>
                <th className="px-4 py-2 sm:px-6 sm:py-3 text-left text-xs font-medium text-secondary uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {users.map((user) => (
                <tr key={user._id} className="hover:bg-gray-50">
                  <td className="px-4 py-2 sm:px-6 sm:py-4 truncate sm:whitespace-nowrap text-sm font-medium text-secondary">{user.email}</td>
                  <td className="px-4 py-2 sm:px-6 sm:py-4 whitespace-nowrap text-sm font-bold text-success">${user.balance.toLocaleString()}</td>
                  <td className="px-4 py-2 sm:px-6 sm:py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      user.isSuspended ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
                    }`}>
                      {user.isSuspended ? 'Suspended' : 'Active'}
                    </span>
                  </td>

                  {/* UPDATED KYC COLUMN */}
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    {user.kyc.status === 'pending' ? (
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleKycAction(user._id, 'approve')}
                          disabled={updating}
                          className="px-2 py-1 bg-success text-white text-xs rounded hover:bg-success/80 transition-colors"
                        >
                          Approve
                        </button>
                        <button
                          onClick={() => handleKycAction(user._id, 'reject')}
                          disabled={updating}
                          className="px-2 py-1 bg-error text-white text-xs rounded hover:bg-error/80 transition-colors"
                        >
                          Reject
                        </button>
                      </div>
                    ) : (
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        user.kyc.status === 'approved' ? 'bg-green-100 text-green-800' :
                        user.kyc.status === 'rejected' ? 'bg-red-100 text-red-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                        {user.kyc.status}
                      </span>
                    )}
                  </td>

                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                    <button
                      onClick={() => openActionModal(user._id, 'fund')}
                      className="px-2 py-1 bg-success text-white rounded-md hover:bg-success/80 transition-colors"
                      disabled={updating}
                    >
                      Fund
                    </button>
                    <button
                      onClick={() => openActionModal(user._id, 'debit')}
                      className="px-2 py-1 bg-error text-white rounded-md hover:bg-error/80 transition-colors"
                      disabled={updating}
                    >
                      Debit
                    </button>
                    <button
                      onClick={() => handleSuspend(user._id)}
                      className="px-2 py-1 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 transition-colors"
                      disabled={updating}
                    >
                      {user.isSuspended ? 'Unsuspend' : 'Suspend'}
                    </button>
                    <button
                      onClick={() => handleDelete(user._id)}
                      className="px-2 py-1 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                      disabled={updating}
                    >
                      Delete
                    </button>
                    <button
                      onClick={() => viewKyc(user)}
                      className="px-2 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                      disabled={updating}
                    >
                      View KYC/Seed
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Action Modal for Fund/Debit */}
        {actionModal.isOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-md">
              <h3 className="text-lg font-semibold text-primary mb-4">
                {actionModal.type === 'debit' ? 'Debit' : 'Fund'} {actionModal.userEmail}
              </h3>
              <div className="space-y-4 mb-4">
                <input
                  type="number"
                  placeholder="Amount (e.g., 100 or 0.05)"
                  className="w-full px-3 py-2 border rounded-md mb-3"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  required
                />
                <select
                  className="w-full px-3 py-2 border rounded-md"
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value)}
                >
                  <option value="USD">USD (Fiat)</option>
                  <option value="BTC">BTC</option>
                  <option value="ETH">ETH</option>
                  <option value="USDT">USDT</option>
                  <option value="XRP">XRP</option>
                  <option value="BNB">BNB</option>
                  <option value="SOL">SOL</option>
                  <option value="USDC">USDC</option>
                </select>
              </div>
              <div className="flex justify-end space-x-4">
                <button
                  onClick={closeActionModal}
                  className="px-4 py-2 bg-gray-200 text-secondary rounded-md"
                >
                  Cancel
                </button>
                <button
                  onClick={handleBalanceUpdate}
                  disabled={updating}
                  className={`px-4 py-2 rounded-md ${updating ? 'bg-gray-400' : 'bg-primary text-white'} ${updating && 'cursor-not-allowed'}`}
                >
                  {updating ? 'Updating...' : actionModal.type === 'debit' ? 'Debit' : 'Fund'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* UPDATED KYC/Seed Modal – Shows Rejection Reason */}
        {kycModal.open && kycModal.user && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-lg max-w-lg w-full mx-4 max-h-96 overflow-y-auto">
              <h3 className="text-xl font-bold text-primary mb-4">KYC & Seed for {kycModal.user.email}</h3>
              <div className="space-y-4 mb-6">
                <p><strong>Status:</strong> 
                  <span className={`ml-2 px-2 py-1 text-xs rounded-full ${
                    kycModal.user.kyc.status === 'approved' ? 'bg-green-100 text-green-800' :
                    kycModal.user.kyc.status === 'rejected' ? 'bg-red-100 text-red-800' :
                    'bg-yellow-100 text-yellow-800'
                  }`}>
                    {kycModal.user.kyc.status}
                  </span>
                </p>
                <p><strong>Name:</strong> {kycModal.user.kyc.details?.fullName || 'N/A'}</p>
                <p><strong>DOB:</strong> {kycModal.user.kyc.details?.dob ? new Date(kycModal.user.kyc.details.dob).toLocaleDateString() : 'N/A'}</p>
                <p><strong>Address:</strong> {kycModal.user.kyc.details?.address || 'N/A'}</p>
                {kycModal.user.kyc.status === 'rejected' && kycModal.user.kyc.reason && (
                  <p className="text-sm text-red-600">
                    <strong>Rejection Reason:</strong> {kycModal.user.kyc.reason}
                  </p>
                )}
                <p className="font-medium"> <strong>Seed Phrase:</strong>{' '} {kycModal.user.seedPhrase ? (
                <span className="font-mono text-xs bg-gray-100 px-2 py-1 rounded break-all">
                {kycModal.user.seedPhrase}
                </span>
                ) : ('Not set')}</p>
                <div className="bg-yellow-100 text-yellow-800 p-2 rounded text-sm">
                  <strong>Warning:</strong> Never display seed in plain text.
                </div>
              </div>
              <button 
                onClick={closeKycModal} 
                className="w-full bg-gray-300 text-gray-700 py-2 rounded-md"
              >
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;